(() => {
  if (customElements.get('hf-personalisation-preview')) return;

  const SIGNER_SECTION_ID = 'hf-personalisation-preview-signer';
  const SUPPLIER_ORIGIN = 'https://preview.personalisedmemento.co.uk';
  const SUPPLIER_PATH = '/dsmo/streamimage.aspx';
  const PRODUCT_HANDLE_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
  const PRODUCT_ID_PATTERN = /^gid:\/\/shopify\/Product\/[1-9]\d*$/;
  const VARIANT_ID_PATTERN = /^[1-9]\d*$/;
  const CONFIGURATION_MD5_PATTERN = /^[a-f0-9]{32}$/;
  const SUPPLIER_SKU_PATTERN = /^[A-Za-z0-9_-]{1,128}$/;
  const FIELD_ID_PATTERN = /^field-([1-9]\d*)$/;
  const CONTROL_OR_FORMAT_PATTERN =
    /[\u0000-\u001F\u007F-\u009F\u200B-\u200F\u2028-\u202E\u2060-\u206F\uFEFF]/u;
  const MAX_ARTWORK_FIELDS = 16;
  const REQUEST_TIMEOUT_MS = 12_000;
  const AUTO_PREVIEW_DELAY_MS = 700;
  const RATE_LIMIT_COOLDOWN_MS = 10_000;

  class HFPersonalisationPreview extends HTMLElement {
    constructor() {
      super();
      this.reconnectSummaryState = null;
      this.automaticPreviewTimer = null;
      this.pendingArtworkFingerprint = '';
      this.lastObservedFingerprint = '';
      this.cooldownUntil = 0;
      this.isComposing = false;
      this.lastReadyFingerprint = '';
    }

    connectedCallback() {
      if (this.initialised) return;
      this.initialised = true;

      this.form = this.closest('form');
      this.productForm = this.closest('product-form');
      this.previewRegion = this.closest('[data-preview-region]');
      this.editor = this.closest('.hf-personalisation-editor');
      this.activationButton = Array.from(
        this.form?.querySelectorAll('[data-preview-activation-button]') || []
      ).find(
        (button) =>
          button.getAttribute('aria-controls') === this.previewRegion?.id
      );
      this.activationWrapper = this.activationButton?.closest(
        '[data-preview-activation-wrapper]'
      );
      this.previewActivated = this.dataset.previewActivated === 'true';
      this.button = this.querySelector('[data-preview-button]');
      this.buttonLabel = this.querySelector('[data-preview-button-label]');
      this.status = this.querySelector('[data-preview-status]');
      this.panel = this.querySelector('[data-preview-panel]');
      this.summaryList = this.querySelector('[data-preview-summary-list]');
      this.fallback = this.querySelector('[data-preview-fallback]');
      this.frame = this.querySelector('[data-preview-frame]');
      this.image = this.querySelector('[data-preview-image]');
      this.placeholder = this.querySelector('[data-preview-placeholder]');
      this.presentation = this.querySelector('[data-preview-presentation]');
      this.progress = this.querySelector('[data-preview-progress]');
      this.productHandle = String(this.dataset.productHandle || '');
      this.productId = String(this.dataset.productId || '');
      this.configurationMd5 = String(this.dataset.configurationMd5 || '');
      this.supplierSku = String(this.dataset.supplierSku || '');
      this.artworkContext = Object.freeze({
        productHandle: this.productHandle,
        productId: this.productId,
        configurationMd5: this.configurationMd5,
        supplierSku: this.supplierSku,
      });
      this.summaryControls = Array.from(
        this.form?.querySelectorAll('[data-preview-summary-field]') || []
      );
      this.controls = this.getOrderedControls();
      this.variantInput = this.form?.querySelector('input[name="id"]');
      this.artworkEnabled = this.dataset.artworkEnabled === 'true';
      this.automaticPreviewTimer = null;
      this.pendingArtworkFingerprint = '';
      this.lastObservedFingerprint = this.interactionFingerprint();
      this.cooldownUntil = 0;
      this.isComposing = false;

      if (
        !this.form ||
        !this.productForm ||
        !this.previewRegion ||
        !this.editor ||
        !this.activationButton ||
        !this.activationWrapper ||
        !this.button ||
        !this.buttonLabel ||
        !this.status ||
        !this.panel ||
        !this.summaryList ||
        !this.fallback ||
        !this.frame ||
        !this.image ||
        !this.placeholder ||
        !this.hasValidSummaryContract()
      ) {
        this.hidden = true;
        return;
      }

      const listenerController = new AbortController();
      this.listenerController = listenerController;
      const listenerOptions = { signal: listenerController.signal };
      this.initDesktopPreview();
      this.presentation?.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape' || !this.desktopDocked) return;
        event.preventDefault();
        this.closePreview();
      }, listenerOptions);
      this.querySelector('[data-preview-close]')?.addEventListener('click', () => this.closePreview(), listenerOptions);
      this.button.addEventListener('click', () => this.handlePreviewClick(), listenerOptions);
      this.activationButton.addEventListener(
        'click',
        () => this.handleActivationClick(),
        listenerOptions
      );
      this.summaryControls.forEach((control) => {
        control.addEventListener('input', () => this.handleFieldChange(control), listenerOptions);
        control.addEventListener('change', () => this.handleFieldChange(control), listenerOptions);
        if (['text', 'textarea'].includes(control.type)) {
          control.addEventListener('compositionstart', () => this.handleCompositionStart(), listenerOptions);
          control.addEventListener('compositionend', () => this.handleCompositionEnd(control), listenerOptions);
        }
      });
      this.variantInput?.addEventListener('change', () => this.handleVariantChange(), listenerOptions);

      if (this.previewActivated) {
        this.dataset.previewActivated = 'true';
        this.editor.dataset.previewActivated = 'true';
        this.previewRegion.hidden = false;
        this.hidden = false;
        this.activationButton.setAttribute('aria-expanded', 'true');
        this.activationWrapper.hidden = true;
        this.restoreSummaryAfterReconnect();
      } else {
        this.hidden = true;
        this.previewRegion.hidden = true;
        this.activationButton.setAttribute('aria-expanded', 'false');
        this.setExpanded(false);
        this.syncActivationAvailability();
      }
      this.syncMobilePreviewActions();

      this.configurationVersion = '';
      this.configurationVersionPromise = null;
      this.contract = null;
      this.artworkContextInitialised = false;
      if (
        !this.artworkEnabled ||
        !window.crypto?.getRandomValues ||
        typeof TextEncoder === 'undefined' ||
        !this.hasValidArtworkContext() ||
        !this.hasSupportedContract()
      ) {
        this.artworkEnabled = false;
        if (this.previewActivated) {
          const rows = this.renderCurrentSummary();
          if (rows.length) {
            this.showFallback();
            this.setStatus(this.dataset.copySummaryReady);
          } else {
            this.hideFallback();
            this.setStatus(this.dataset.copyIdle);
          }
        } else {
          this.setExpanded(false);
        }
        return;
      }

      this.contract = this.buildContract();
      this.artworkContextInitialised = true;
      this.configurationVersion = this.configurationMd5;
      this.configurationVersionPromise = Promise.resolve(this.configurationMd5);
      if (!this.previewActivated) return;
      this.setExpanded(true);
      this.showPlaceholderFrame();
      this.setStatus(this.dataset.copyIdle);
    }

    disconnectedCallback() {
      this.destroyDesktopPreview();
      this.captureSummaryForReconnect();
      this.setExpanded(false);
      this.summaryList?.replaceChildren();
      this.hideFallback();
      this.clearStale();
      this.interactionSequence = (this.interactionSequence || 0) + 1;
      this.cancelAutomaticPreview();
      this.abortActiveRequest();
      this.listenerController?.abort();
      this.requestSequence = (this.requestSequence || 0) + 1;
      this.clearImage();
      this.setLoading(false);
      this.setStatus(this.dataset.copyIdle);
      this.configurationVersion = '';
      this.configurationVersionPromise = null;
      this.contract = null;
      this.artworkContextInitialised = false;
      this.artworkContext = null;
      this.cooldownUntil = 0;
      this.lastObservedFingerprint = '';
      this.pendingArtworkFingerprint = '';
      this.isComposing = false;
      this.hidden = true;
      this.initialised = false;
    }

    captureSummaryForReconnect() {
      if (this.reconnectSummaryState) return;
      const nodes =
        this.summaryList?.childNodes
        || this.summaryList?.children
        || [];
      this.reconnectSummaryState = {
        expanded: Boolean(this.panel && !this.panel.hidden),
        nodes: Array.from(nodes),
      };
    }

    restoreSummaryAfterReconnect() {
      const state = this.reconnectSummaryState;
      if (!state) return;
      this.reconnectSummaryState = null;
      this.summaryList?.replaceChildren(...state.nodes);
      this.setExpanded(state.expanded);
    }

    async handleActivationClick() {
      if (this.previewActivated) return;
      await this.handlePreviewClick({ activate: true });
    }

    activatePreview() {
      if (this.artworkEnabled && !this.ownerDocument.head.querySelector(`link[rel="preconnect"][href="${SUPPLIER_ORIGIN}"]`)) {
        const connectionHint = this.ownerDocument.createElement('link');
        connectionHint.rel = 'preconnect';
        connectionHint.href = SUPPLIER_ORIGIN;
        this.ownerDocument.head.append(connectionHint);
      }
      this.previewActivated = true;
      this.dataset.previewActivated = 'true';
      this.editor.dataset.previewActivated = 'true';
      this.previewRegion.hidden = false;
      this.hidden = false;
      this.activationButton.setAttribute('aria-expanded', 'true');
      this.focusActivatedPreview();
    }

    focusActivatedPreview() {
      if (this.artworkEnabled) this.setSupplierPreviewPresentation('loading');
      if (!this.mobileActionsActive) this.button.focus({ preventScroll: true });
      this.activationWrapper.hidden = true;
      (this.desktopDocked ? this.desktopDock : this.previewRegion).scrollIntoView({
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches
          ? 'auto'
          : 'smooth',
        block: 'start',
      });
    }

    async handlePreviewClick({ activate = false } = {}) {
      if (
        !activate
        && (this.previewActivated === false || this.previewBusy)
      ) return false;
      this.cancelAutomaticPreview();
      const interactionSequence = (this.interactionSequence || 0) + 1;
      this.interactionSequence = interactionSequence;
      if (!this.validateSummaryFields({ announce: !activate })) return false;

      const rows = this.buildSummaryRows();
      if (!rows.length) {
        if (activate) {
          this.focusFirstSummaryControl();
          return false;
        }
        this.setExpanded(false);
        this.summaryList.replaceChildren();
        this.clearStale();
        this.setStatus(this.dataset.copyEmpty);
        this.focusFirstSummaryControl();
        return false;
      }

      if (activate) this.activatePreview();
      this.renderSummary(rows);
      this.setExpanded(true);
      if (activate && this.mobileActionsActive) this.panel.focus({ preventScroll: true });
      this.clearStale();
      this.hideFallback();
      this.setStatus(this.dataset.copySummaryReady);

      if (this.isComposing) {
        if (this.artworkEnabled) this.showPlaceholderFrame();
        return;
      }

      if (
        this.artworkEnabled &&
        this.configurationVersionPromise &&
        !this.configurationVersion
      ) {
        try {
          const version = await this.configurationVersionPromise;
          if (
            interactionSequence !== this.interactionSequence ||
            !this.isConnected
          ) {
            return;
          }
          if (typeof version === 'string' && version) {
            this.configurationVersion = version;
          }
        } catch {
          if (
            interactionSequence !== this.interactionSequence ||
            !this.isConnected
          ) {
            return;
          }
          this.artworkEnabled = false;
        }
      }

      if (
        interactionSequence !== this.interactionSequence ||
        !this.isConnected
      ) {
        return;
      }

      if (this.canRequestArtwork()) {
        if (activate && this.hasReusableArtwork()) {
          this.placeholder.hidden = true;
          this.image.hidden = false;
          this.frame.hidden = false;
          this.setSupplierPreviewPresentation('ready');
          this.setLoading(false);
          this.setStatus(this.dataset.copyReady);
          return;
        }
        if (this.now() < this.cooldownUntil) {
          this.clearImage();
          this.showPlaceholderFrame();
          this.showFallback();
          this.setStatus(this.dataset.copyRateLimited);
          return;
        }
        await this.createArtworkPreview();
      } else {
        this.showFallback();
      }
    }

    getOrderedControls() {
      if (!this.form) return [];

      return Array.from(this.form.querySelectorAll('[data-preview-field-id]')).sort((left, right) => {
        const leftIndex = Number(FIELD_ID_PATTERN.exec(left.dataset.previewFieldId || '')?.[1] || 0);
        const rightIndex = Number(FIELD_ID_PATTERN.exec(right.dataset.previewFieldId || '')?.[1] || 0);
        return leftIndex - rightIndex;
      });
    }

    hasValidSummaryContract() {
      if (!this.summaryControls?.length) return false;

      const ids = new Set();
      const properties = new Set();

      return this.summaryControls.every((control) => {
        const id = String(control.dataset.previewSummaryId || '').trim();
        const label = String(control.dataset.previewSummaryLabel || '').trim();
        const type = control.dataset.previewSummaryType;
        const actualType =
          control.type === 'file'
            ? 'file'
            : control.type === 'textarea'
              ? 'textarea'
              : control.type === 'text' && control.dataset.personalisationType === 'number'
                ? 'number'
                : control.type === 'text'
                  ? 'text'
                  : '';
        const property = String(control.name || '');
        const invalid = control.hasAttribute('data-personalisation-contract-invalid');

        if (
          !id ||
          ids.has(id) ||
          !label ||
          !['text', 'textarea', 'number', 'file'].includes(type) ||
          type !== actualType ||
          !property.startsWith('properties[') ||
          properties.has(property) ||
          invalid
        ) {
          return false;
        }

        ids.add(id);
        properties.add(property);
        return true;
      });
    }

    buildSummaryRows() {
      return this.summaryControls.flatMap((control) => {
        const id = this.normalise(control.dataset.previewSummaryId);
        const label = this.normalise(control.dataset.previewSummaryLabel);
        const type = control.dataset.previewSummaryType === 'file' ? 'file' : 'text';

        if (!id || !label) return [];

        if (type === 'file') {
          if (!control.files?.length) return [];
          return [{ id, label, type, value: this.dataset.copyPhotoSelected }];
        }

        const value = String(control.value ?? '');
        if (!value.trim().length) return [];
        return [{ id, label, type, value }];
      });
    }

    // Move only presentation nodes. The custom element and every order input stay in the form.
    initDesktopPreview() {
      if ((!this.customAssigned && !this.artworkEnabled) || !this.presentation || !window.matchMedia) return;
      this.desktopMedia = window.matchMedia('(min-width: 990px)');
      this.desktopMediaChange = () => this.syncDesktopPreview();
      this.desktopMedia.addEventListener('change', this.desktopMediaChange);
      this.initMobilePreviewActions();
      const product = this.closest('product-info');
      if (!product || (this.customAssigned && !product.querySelector('hf-photo-upload'))) return;
      const gallery = product.querySelector('.product__media-wrapper > media-gallery');
      const viewer = gallery?.querySelector(':scope > slider-component[id^="GalleryViewer-"]');
      if (!viewer || gallery.closest('.product--no-media')) return;
      this.desktopGallery = gallery;
      this.desktopViewer = viewer;
      this.presentationAnchor = this.ownerDocument.createComment('preview presentation');
      this.presentation.before(this.presentationAnchor);
      this.desktopDock = this.ownerDocument.createElement('div');
      this.desktopDock.className = 'hf-desktop-preview-dock';
      this.desktopDock.hidden = true;
      viewer.append(this.desktopDock);
    }

    initMobilePreviewActions() {
      this.mobileActionAnchors = [this.button, this.status].map(node => {
        const anchor = this.ownerDocument.createComment('preview action');
        node.before(anchor);
        return { node, anchor };
      });
      this.mobileActions = this.ownerDocument.createElement('div');
      this.mobileActions.className = 'hf-personalisation-preview__mobile-actions';
      this.mobileActions.hidden = true;
      this.mobileProgress = this.ownerDocument.createElement('div');
      this.mobileProgress.className = 'hf-personalisation-preview__progress';
      this.mobileProgress.setAttribute('data-preview-mobile-progress', '');
      this.mobileProgress.setAttribute('aria-hidden', 'true');
      this.mobileProgress.hidden = true;
      this.mobileActions.append(this.mobileProgress);
      this.editor.append(this.mobileActions);
    }

    syncMobilePreviewActions({ restore = false } = {}) {
      if (!this.mobileActions) return;
      const active = !restore && this.previewActivated && !this.desktopMedia.matches;
      if (active === Boolean(this.mobileActionsActive)) return;
      const focused = this.ownerDocument.activeElement;
      const restoreFocus = this.mobileActionAnchors.some(({ node }) => node.contains(focused));
      if (active) {
        this.mobileActions.hidden = false;
        this.mobileActionAnchors.forEach(({ node }) => this.mobileActions.append(node));
      } else {
        this.mobileActionAnchors.forEach(({ node, anchor }) => anchor.after(node));
        this.mobileActions.hidden = true;
      }
      this.mobileActionsActive = active;
      this.mobileActions.setAttribute('aria-busy', String(Boolean(this.previewBusy)));
      if (restoreFocus && focused.isConnected) focused.focus({ preventScroll: true });
    }

    destroyMobilePreviewActions() {
      this.syncMobilePreviewActions({ restore: true });
      this.mobileActionAnchors?.forEach(({ anchor }) => anchor.remove());
      this.mobileActions?.remove();
      this.mobileActionAnchors = null;
      this.mobileActions = null;
      this.mobileProgress = null;
      this.mobileActionsActive = false;
    }

    setCustomPreviewPresentation(state) {
      this.customPresentationState = state;
      this.syncDesktopPreview();
    }

    setSupplierPreviewPresentation(state) {
      this.supplierPresentationState = state;
      this.syncDesktopPreview();
    }

    syncDesktopPreview() {
      this.syncMobilePreviewActions();
      if (!this.desktopDock || !this.presentationAnchor) return;
      const shouldDock = this.desktopMedia.matches && this.previewActivated
        && !this.desktopGallery.hidden
        && (this.customAssigned
          ? this.dataset.customPreviewEnabled === 'true' && ['loading', 'ready'].includes(this.customPresentationState)
          : this.artworkEnabled && ['loading', 'ready'].includes(this.supplierPresentationState));
      if (shouldDock === Boolean(this.desktopDocked)) return;
      const focused = this.ownerDocument.activeElement;
      const restoreFocus = this.presentation.contains(focused);
      if (shouldDock) {
        this.viewerHadOverlayClass = this.desktopViewer.classList.contains('hf-preview-overlay-host');
        this.desktopViewer.classList.add('hf-preview-overlay-host');
        const background = [
          ...Array.from(this.desktopGallery.children).filter(node => node !== this.desktopViewer),
          ...Array.from(this.desktopViewer.children).filter(node => node !== this.desktopDock),
        ];
        this.originalGalleryState = background.map(node => ({
          node, inert: node.getAttribute('inert'), ariaHidden: node.getAttribute('aria-hidden'),
        }));
        this.originalGalleryState.forEach(({ node }) => {
          node.setAttribute('inert', '');
          node.setAttribute('aria-hidden', 'true');
        });
        this.desktopDock.hidden = false;
        this.desktopDock.append(this.presentation);
        if (this.customCanvas) {
          this.originalCanvasAttributes = ['tabindex', 'role', 'aria-label'].map(name => [name, this.customCanvas.getAttribute(name)]);
          this.customCanvas.setAttribute('tabindex', '0');
          this.customCanvas.setAttribute('role', 'region');
          this.customCanvas.setAttribute('aria-label', 'Product preview');
        }
        this.editor.dataset.desktopPreviewDocked = 'true';
        if (background.some(node => node.contains(focused))) this.button.focus({ preventScroll: true });
      } else {
        this.presentationAnchor.after(this.presentation);
        this.originalGalleryState?.forEach(({ node, inert, ariaHidden }) => {
          if (inert === null) node.removeAttribute('inert'); else node.setAttribute('inert', inert);
          if (ariaHidden === null) node.removeAttribute('aria-hidden'); else node.setAttribute('aria-hidden', ariaHidden);
        });
        this.originalGalleryState = null;
        this.originalCanvasAttributes?.forEach(([name, value]) => {
          if (value === null) this.customCanvas.removeAttribute(name); else this.customCanvas.setAttribute(name, value);
        });
        this.originalCanvasAttributes = null;
        if (!this.viewerHadOverlayClass) this.desktopViewer.classList.remove('hf-preview-overlay-host');
        this.desktopDock.hidden = true;
        delete this.editor.dataset.desktopPreviewDocked;
      }
      this.desktopDocked = shouldDock;
      if (restoreFocus) focused.focus({ preventScroll: true });
    }

    destroyDesktopPreview() {
      this.supplierPresentationState = 'closed';
      this.setCustomPreviewPresentation('closed');
      this.destroyMobilePreviewActions();
      this.desktopMedia?.removeEventListener('change', this.desktopMediaChange);
      this.desktopDock?.remove();
      this.presentationAnchor?.remove();
      this.desktopDock = null;
      this.presentationAnchor = null;
      this.desktopMedia = null;
      this.desktopGallery = null;
      this.desktopViewer = null;
      this.desktopMediaChange = null;
    }

    closePreview() {
      const retainReadyArtwork = this.hasReusableArtwork();
      this.destroyCustomPreview?.();
      this.cancelAutomaticPreview();
      this.abortActiveRequest();
      this.setLoading(false);
      this.clearImage({ retainReadyArtwork });
      this.previewActivated = false;
      this.syncMobilePreviewActions();
      this.dataset.previewActivated = 'false';
      this.editor.dataset.previewActivated = 'false';
      this.setExpanded(false);
      this.previewRegion.hidden = true;
      this.hidden = true;
      this.activationButton.setAttribute('aria-expanded', 'false');
      this.activationWrapper.hidden = false;
      this.activationButton.focus({ preventScroll: true });
      this.syncActivationAvailability();
      if (this.activationWrapper.hidden) this.focusFirstSummaryControl();
    }

    hasSummaryValue(control) {
      if (!control) return false;
      if (control.dataset.previewSummaryType === 'file') {
        return Boolean(control.files?.length);
      }
      return String(control.value ?? '').trim().length > 0;
    }

    requiredSummaryFieldsReady() {
      const requiredControls = this.summaryControls.filter((control) =>
        control.hasAttribute('data-personalisation-required')
      );
      const requiredReady = requiredControls.every((control) =>
        this.hasSummaryValue(control) && control.checkValidity()
      );
      return requiredReady && this.summaryControls.some((control) => this.hasSummaryValue(control));
    }

    syncActivationAvailability() {
      if (!this.activationWrapper || this.previewActivated) return;
      this.activationWrapper.hidden = !this.requiredSummaryFieldsReady();
    }

    renderCurrentSummary() {
      const rows = this.buildSummaryRows();
      if (!rows.length) {
        this.summaryList.replaceChildren();
        this.setExpanded(this.artworkEnabled);
        return rows;
      }

      this.renderSummary(rows);
      this.setExpanded(true);
      return rows;
    }

    renderSummary(rows) {
      const fragment = document.createDocumentFragment();

      rows.forEach((row) => {
        const item = document.createElement('div');
        item.className = 'hf-personalisation-preview__summary-row';

        const term = document.createElement('dt');
        term.textContent = row.label;

        const value = document.createElement('dd');
        value.textContent = row.value;

        item.append(term, value);
        fragment.append(item);
      });

      this.summaryList.replaceChildren(fragment);
    }

    setExpanded(expanded) {
      if (!this.panel || !this.button) return;
      this.panel.hidden = !expanded;
      this.button.setAttribute('aria-expanded', String(expanded));
      if (!expanded) this.setSupplierPreviewPresentation('closed');
    }

    showFallback() {
      if (!this.customAssigned) this.setSupplierPreviewPresentation('fallback');
      if (this.fallback) this.fallback.hidden = false;
    }

    hideFallback() {
      if (this.fallback) this.fallback.hidden = true;
    }

    validateSummaryFields({ announce = true } = {}) {
      if (typeof this.productForm?.validatePersonalisation !== 'function') {
        if (announce) this.setStatus(this.dataset.copyUnavailable);
        return false;
      }

      const valid = Boolean(
        this.productForm?.validatePersonalisation({ includeConfirmation: false })
      );
      if (!valid && announce) this.setStatus(this.dataset.copyInvalid);
      return valid;
    }

    focusFirstSummaryControl() {
      const control = this.summaryControls?.[0];
      if (!control) return;
      const disclosure = control.closest('details');
      if (disclosure) disclosure.open = true;
      control.focus({ preventScroll: true });
    }

    hasSupportedContract() {
      return Boolean(this.buildContract());
    }

    canRequestArtwork() {
      const requiresConfigurationMatch =
        this.artworkContextInitialised === true
        || this.configurationMd5 !== undefined;
      return Boolean(
        this.artworkEnabled &&
        this.configurationVersion &&
        (
          !requiresConfigurationMatch
          || (
            CONFIGURATION_MD5_PATTERN.test(this.configurationMd5)
            && this.configurationVersion === this.configurationMd5
          )
        ) &&
        this.hasSupportedContract()
      );
    }

    buildContract() {
      if (!this.controls?.length || this.controls.length > MAX_ARTWORK_FIELDS) {
        return null;
      }

      const ids = new Set();
      const properties = new Set();
      const fields = [];
      let configuredMaximum = 0;

      for (const [index, control] of this.controls.entries()) {
        const id = String(control.dataset.previewFieldId || '');
        const match = FIELD_ID_PATTERN.exec(id);
        const type = String(control.dataset.previewFieldType || '');
        const effectiveProperty = String(
          control.dataset.previewEffectiveProperty || ''
        );
        const requiredValue = String(control.dataset.previewRequired || '');
        const minLength = this.toCanonicalInteger(
          control.dataset.previewMinlength,
          true
        );
        const maxLength = this.toCanonicalInteger(
          control.dataset.previewMaxlength,
          false
        );
        const pattern = String(control.dataset.previewPattern || '');

        if (
          !match ||
          Number(match[1]) !== index + 1 ||
          ids.has(id) ||
          !['text', 'number', 'textarea'].includes(type) ||
          this.getControlType(control) !== type ||
          !effectiveProperty.trim() ||
          properties.has(effectiveProperty) ||
          !['true', 'false'].includes(requiredValue) ||
          minLength === null ||
          maxLength === null ||
          minLength > maxLength ||
          maxLength > 300 ||
          pattern !== ''
        ) {
          return null;
        }

        configuredMaximum += maxLength;
        if (configuredMaximum > 500) return null;

        ids.add(id);
        properties.add(effectiveProperty);
        fields.push({
          id,
          type,
          required: requiredValue === 'true',
          minLength,
          maxLength,
          effectiveProperty,
        });
      }

      return fields;
    }

    getControlType(control) {
      if (control.type === 'textarea') return 'textarea';
      if (control.type !== 'text') return '';
      return control.dataset.personalisationType === 'number' ? 'number' : 'text';
    }

    hasValidArtworkContext() {
      const context = this.artworkContext || {
        productHandle: this.productHandle,
        productId: this.productId,
        configurationMd5: this.configurationMd5,
        supplierSku: this.supplierSku,
      };

      return Boolean(
        this.productHandle === context.productHandle
        && this.productId === context.productId
        && this.configurationMd5 === context.configurationMd5
        && this.supplierSku === context.supplierSku
        && PRODUCT_HANDLE_PATTERN.test(context.productHandle)
        && PRODUCT_ID_PATTERN.test(context.productId)
        && CONFIGURATION_MD5_PATTERN.test(context.configurationMd5)
        && SUPPLIER_SKU_PATTERN.test(context.supplierSku)
        && context.supplierSku === context.supplierSku.trim()
      );
    }

    async createArtworkPreview({
      interactive = true,
      expectedFingerprint = '',
    } = {}) {
      if (!this.configurationVersion) {
        this.clearImage();
        this.showFallback();
        this.setStatus(this.dataset.copyUnavailable);
        return;
      }
      if (!this.validatePreviewFields({ interactive })) {
        this.clearImage();
        this.showFallback();
        return;
      }

      const variantInput = this.form.querySelector('input[name="id"]');
      const numericVariantId = variantInput?.value || '';
      if (!VARIANT_ID_PATTERN.test(numericVariantId) || variantInput.disabled) {
        this.clearImage();
        this.showFallback();
        this.setStatus(this.dataset.copyUnavailable);
        return;
      }

      const currentFingerprint = this.artworkFingerprint(numericVariantId);
      if (expectedFingerprint && expectedFingerprint !== currentFingerprint) return;

      const payload = this.createSignerPayload(numericVariantId);
      if (!payload) {
        this.clearImage();
        this.showFallback();
        this.setStatus(this.dataset.copyUnavailable);
        return;
      }

      this.abortActiveRequest();
      this.clearImage();
      this.hideFallback();
      this.requestSequence = (this.requestSequence || 0) + 1;
      const requestSequence = this.requestSequence;
      const requestFingerprint = currentFingerprint;
      const controller = new AbortController();
      this.activeController = controller;
      let timedOut = false;
      const timeoutId = window.setTimeout(() => {
        timedOut = true;
        controller.abort();
      }, REQUEST_TIMEOUT_MS);

      this.setLoading(true);
      this.setStatus(this.dataset.copyLoading);
      this.showLoadingFrame();

      try {
        const response = await fetch(this.createSignerRequestUrl(payload), {
          method: 'GET',
          credentials: 'same-origin',
          cache: 'no-store',
          redirect: 'error',
          referrerPolicy: 'no-referrer',
          signal: controller.signal,
        });

        if (
          requestSequence !== this.requestSequence
          || requestFingerprint !== this.artworkFingerprint(numericVariantId)
          || controller.signal.aborted
        ) {
          return;
        }

        if (!response.ok) {
          if (response.status === 429) {
            this.cooldownUntil = this.now() + RATE_LIMIT_COOLDOWN_MS;
            this.clearImage();
            this.showPlaceholderFrame();
            this.showFallback();
            this.setStatus(this.dataset.copyRateLimited);
          } else {
            this.clearImage();
            this.showFallback();
            this.setStatus(this.dataset.copyUnavailable);
          }
          return;
        }

        const signedUrl = await this.readSignerResponse(response, payload.values);
        if (
          requestSequence !== this.requestSequence
          || requestFingerprint !== this.artworkFingerprint(numericVariantId)
          || controller.signal.aborted
        ) {
          return;
        }

        await this.loadImage(
          signedUrl,
          requestSequence,
          requestFingerprint,
          numericVariantId,
          controller.signal
        );
        if (
          requestSequence !== this.requestSequence
          || requestFingerprint !== this.artworkFingerprint(numericVariantId)
          || controller.signal.aborted
        ) {
          return;
        }
        this.hideFallback();
        this.setStatus(this.dataset.copyReady);
      } catch (error) {
        if (
          requestSequence !== this.requestSequence
          || requestFingerprint !== this.artworkFingerprint(numericVariantId)
        ) {
          return;
        }
        if (error?.name === 'AbortError' && !timedOut) return;
        this.clearImage();
        this.showFallback();
        this.setStatus(this.dataset.copyUnavailable);
      } finally {
        window.clearTimeout(timeoutId);
        if (requestSequence === this.requestSequence) {
          this.activeController = null;
          this.setLoading(false);
        }
      }
    }

    validatePreviewFields({ interactive = true } = {}) {
      const contractById = new Map(
        (this.contract || this.buildContract() || []).map((field) => [field.id, field])
      );

      for (const control of this.controls) {
        if (interactive) this.clearPreviewInvalid(control);
        const value = String(control.value ?? '');
        const contract = contractById.get(control.dataset.previewFieldId);
        let invalid = Boolean(
          !contract
          || value !== value.trim()
          || CONTROL_OR_FORMAT_PATTERN.test(value)
          || /[;=${}]/.test(value)
          || (contract?.required && !value)
        );

        if (!invalid && value && value.length < contract.minLength) {
          invalid = true;
        }
        if (!invalid && value && value.length > contract.maxLength) {
          invalid = value.length > contract.maxLength;
        }
        if (!invalid && value && contract.type === 'number' && !/^\d+$/.test(value)) {
          invalid = true;
        }
        if (!invalid && !control.checkValidity()) invalid = true;

        if (invalid) {
          if (interactive) {
            this.markPreviewInvalid(control);
            this.setStatus(this.dataset.copyInvalid || this.dataset.copyUnavailable);
            const disclosure = control.closest('details');
            if (disclosure) disclosure.open = true;
            control.focus({ preventScroll: true });
            control.scrollIntoView({
              behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches
                ? 'auto'
                : 'smooth',
              block: 'center',
            });
          }
          return false;
        }
      }

      if (
        !this.controls.some(
          (control) => this.previewValue(control).length > 0
        )
      ) {
        if (interactive) {
          this.setStatus(this.dataset.copyEmpty);
          const disclosure = this.controls[0].closest('details');
          if (disclosure) disclosure.open = true;
          this.controls[0].focus();
        }
        return false;
      }

      return true;
    }

    handleFieldChange(control, { force = false } = {}) {
      if (control) this.clearPreviewInvalid(control);
      if (!this.previewActivated) this.syncActivationAvailability();
      const fingerprint = this.interactionFingerprint();
      if (!force && fingerprint === this.lastObservedFingerprint) return;
      this.lastObservedFingerprint = fingerprint;
      if (!this.previewActivated) {
        this.clearImage();
        return;
      }

      this.cancelAutomaticPreview();
      this.interactionSequence = (this.interactionSequence || 0) + 1;
      this.abortActiveRequest();
      this.clearImage();
      this.setLoading(false);
      this.clearStale();
      this.hideFallback();

      const rows = this.renderCurrentSummary();
      if (!rows.length) {
        if (this.artworkEnabled) this.showPlaceholderFrame();
        this.setStatus(this.dataset.copyIdle);
        return;
      }

      if (!this.canRequestArtwork()) {
        this.showFallback();
        this.setStatus(this.dataset.copySummaryReady);
        return;
      }

      this.showPlaceholderFrame();
      this.setStatus(this.dataset.copyChanged);
      if (!this.isComposing) this.scheduleAutomaticPreview();
    }

    handleVariantChange() {
      this.cancelAutomaticPreview();
      this.cooldownUntil = 0;
      this.lastObservedFingerprint = '';
      this.handleFieldChange(null, { force: true });
    }

    handleCompositionStart() {
      this.isComposing = true;
      this.cancelAutomaticPreview();
      this.abortActiveRequest();
      this.setLoading(false);
    }

    handleCompositionEnd(control) {
      this.isComposing = false;
      this.handleFieldChange(control, { force: true });
    }

    markStale() {
      this.interactionSequence = (this.interactionSequence || 0) + 1;
      if (!this.panel || this.panel.hidden) return;
      this.abortActiveRequest();
      this.clearImage();
      this.hideFallback();
      this.panel.dataset.previewStale = 'true';
      this.setLoading(false);
      this.setStatus(this.dataset.copyChanged);
    }

    clearStale() {
      if (this.panel) delete this.panel.dataset.previewStale;
    }

    markPreviewInvalid(control) {
      control.dataset.previewInvalid = 'true';
      if (!control.hasAttribute('aria-invalid')) {
        control.dataset.previewOwnsAriaInvalid = 'true';
        control.setAttribute('aria-invalid', 'true');
      }
      control
        .closest('[data-personalisation-item]')
        ?.classList.add('is-preview-invalid');
    }

    clearPreviewInvalid(control) {
      if (control.dataset.previewInvalid !== 'true') return;
      delete control.dataset.previewInvalid;
      control
        .closest('[data-personalisation-item]')
        ?.classList.remove('is-preview-invalid');
      if (control.dataset.previewOwnsAriaInvalid === 'true') {
        delete control.dataset.previewOwnsAriaInvalid;
        if (control.checkValidity()) control.removeAttribute('aria-invalid');
      }
    }

    async loadImage(
      supplierUrl,
      requestSequence,
      requestFingerprint,
      numericVariantId,
      signal
    ) {
      await new Promise((resolve, reject) => {
        const handleLoad = () => {
          cleanup();
          if (
            requestSequence !== this.requestSequence
            || requestFingerprint !== this.artworkFingerprint(numericVariantId)
            || signal?.aborted
          ) {
            reject(new DOMException('Stale request', 'AbortError'));
            return;
          }
          this.placeholder.hidden = true;
          this.image.hidden = false;
          this.frame.hidden = false;
          this.lastReadyFingerprint = requestFingerprint;
          this.setSupplierPreviewPresentation('ready');
          resolve();
        };
        const handleError = () => {
          cleanup();
          reject(new Error('Preview image unavailable'));
        };
        const handleAbort = () => {
          cleanup();
          this.image.removeAttribute('src');
          reject(new DOMException('Preview image request aborted', 'AbortError'));
        };
        const cleanup = () => {
          this.image.removeEventListener('load', handleLoad);
          this.image.removeEventListener('error', handleError);
          signal?.removeEventListener('abort', handleAbort);
        };

        if (signal?.aborted) {
          handleAbort();
          return;
        }
        this.image.addEventListener('load', handleLoad, { once: true });
        this.image.addEventListener('error', handleError, { once: true });
        signal?.addEventListener('abort', handleAbort, { once: true });
        this.image.referrerPolicy = 'no-referrer';
        this.image.src = supplierUrl;
      });
    }

    createSignerPayload(numericVariantId) {
      const productNumericId = PRODUCT_ID_PATTERN.exec(this.productId)?.[0]?.split('/').pop();
      const rawValues = this.controls.map((control) => String(control.value ?? ''));
      const values = rawValues.map((value) => value.trim().length > 0 ? value : '');
      const contract = this.contract || this.buildContract();
      if (
        !this.hasValidArtworkContext()
        || !this.hasSupportedContract()
        || !contract
        || !productNumericId
        || !VARIANT_ID_PATTERN.test(numericVariantId)
        || this.configurationVersion !== this.configurationMd5
        || values.length < 1
        || values.length > MAX_ARTWORK_FIELDS
        || !values.some((value) => value)
        || rawValues.some(
          (value) =>
            value !== value.trim()
            || CONTROL_OR_FORMAT_PATTERN.test(value)
            || /[;=$\{\}]/.test(value)
        )
        || rawValues.some(
          (value, index) =>
            value
            && contract[index].type === 'number'
            && !/^\d+$/.test(value)
        )
      ) {
        return null;
      }

      let nonce;
      try {
        const nonceBytes = new Uint8Array(18);
        window.crypto.getRandomValues(nonceBytes);
        nonce = this.toUrlSafeBase64(nonceBytes, false);
      } catch {
        return null;
      }

      return {
        values,
        value: [
          'hf2',
          nonce,
          this.productHandle,
          productNumericId,
          numericVariantId,
          this.configurationVersion,
          String(values.length),
          ...values.map((value) => this.toUrlSafeBase64(new TextEncoder().encode(value), true)),
        ].join('~'),
      };
    }

    toUrlSafeBase64(bytes, padded) {
      let binary = '';
      bytes.forEach((byte) => {
        binary += String.fromCharCode(byte);
      });
      const encoded = window.btoa(binary).replace(/\+/g, '-').replace(/\//g, '_');
      return padded ? encoded : encoded.replace(/=+$/g, '');
    }

    createSignerRequestUrl(payload) {
      const root =
        typeof window.Shopify?.routes?.root === 'string'
          ? window.Shopify.routes.root
          : '/';
      const url = new URL(
        `${root.replace(/\/?$/, '/')}search/suggest`,
        window.location.origin
      );
      url.searchParams.set('q', payload.value);
      url.searchParams.set('resources[type]', 'product');
      url.searchParams.set('resources[limit]', '1');
      url.searchParams.set('section_id', SIGNER_SECTION_ID);
      return url.toString();
    }

    async readSignerResponse(response, values) {
      const responseUrl = new URL(response.url || window.location.href, window.location.origin);
      const requestUrl = new URL(this.createSignerRequestUrl(''));
      if (
        responseUrl.origin !== requestUrl.origin
        || responseUrl.pathname !== requestUrl.pathname
      ) {
        throw new Error('Signer response must be same-origin');
      }
      const source = await response.text();
      const document = new DOMParser().parseFromString(source, 'text/html');
      const signedMarkers = document.querySelectorAll('[data-hf-personalisation-preview-signed-url]');
      const errorMarkers = document.querySelectorAll('[data-hf-personalisation-preview-error]');
      if (signedMarkers.length !== 1 || errorMarkers.length !== 0) {
        throw new Error('Signer response is invalid');
      }
      const signedUrl = signedMarkers[0].getAttribute('data-hf-personalisation-preview-signed-url');
      if (!this.isValidSupplierUrl(signedUrl, values)) {
        throw new Error('Supplier URL is invalid');
      }
      return signedUrl;
    }

    isValidSupplierUrl(value, requestedValues) {
      if (!value) return false;
      let url;
      try {
        url = new URL(value);
      } catch {
        return false;
      }
      const isExpectedSupplierOrigin = url.origin === SUPPLIER_ORIGIN;
      if (
        url.protocol !== 'https:'
        || !isExpectedSupplierOrigin
        || url.pathname !== SUPPLIER_PATH
        || url.port
        || url.username
        || url.password
        || url.hash
      ) return false;
      const keys = ['set', 't', 'pw', 'co', 'u', 'Seal', 'err'];
      if (url.searchParams.size !== keys.length || keys.some((key) => url.searchParams.getAll(key).length !== 1)) return false;
      if (Array.from(url.searchParams.keys()).some((key) => !keys.includes(key))) return false;
      if (
        url.searchParams.get('set') !== this.supplierSku
        || url.searchParams.get('pw') !== '600'
        || url.searchParams.get('co') !== '80'
        || url.searchParams.get('u') !== '8F64612C'
        || !/^[A-Fa-f0-9]{32}$/.test(url.searchParams.get('Seal') || '')
        || url.searchParams.get('err') !== 'txt'
      ) return false;
      const expectedLines = requestedValues.flatMap((requestedValue, index) =>
        requestedValue ? [`Line${index + 1}=${requestedValue}`] : []
      );
      const expectedPersonalisation = `Pers{$${expectedLines.join(';')}$}`;
      return (
        expectedLines.length > 0
        && url.searchParams.get('t') === expectedPersonalisation
      );
    }

    abortActiveRequest() {
      if (!this.activeController) return;
      this.requestSequence = (this.requestSequence || 0) + 1;
      this.activeController.abort();
      this.activeController = null;
    }

    cancelAutomaticPreview() {
      if (this.automaticPreviewTimer !== null) {
        window.clearTimeout(this.automaticPreviewTimer);
        this.automaticPreviewTimer = null;
        this.setLoading(false);
      }
      this.pendingArtworkFingerprint = '';
    }

    scheduleAutomaticPreview() {
      this.cancelAutomaticPreview();
      if (this.isComposing || !this.canRequestArtwork()) return;
      if (this.now() < this.cooldownUntil) return;
      if (!this.validatePreviewFields({ interactive: false })) return;

      const variantId = this.variantInput?.value || '';
      if (!VARIANT_ID_PATTERN.test(variantId) || this.variantInput.disabled) return;
      const fingerprint = this.artworkFingerprint(variantId);
      this.pendingArtworkFingerprint = fingerprint;
      this.automaticPreviewTimer = window.setTimeout(() => {
        this.automaticPreviewTimer = null;
        this.pendingArtworkFingerprint = '';
        this.setLoading(false);
        void this.createArtworkPreview({
          interactive: false,
          expectedFingerprint: fingerprint,
        });
      }, AUTO_PREVIEW_DELAY_MS);
      this.setLoading(true);
    }

    interactionFingerprint() {
      const summaryValues = this.summaryControls.map((control) => {
        if (control.dataset.previewSummaryType === 'file') {
          return control.files?.length ? 'file:selected' : 'file:empty';
        }
        return String(control.value ?? '');
      });
      return JSON.stringify([this.variantInput?.value || '', ...summaryValues]);
    }

    artworkFingerprint(numericVariantId) {
      return JSON.stringify([
        this.configurationMd5,
        numericVariantId,
        ...this.controls.map((control) => this.previewValue(control)),
      ]);
    }

    hasReusableArtwork() {
      const variantId = this.variantInput?.value || '';
      return Boolean(
        !this.previewBusy && !this.isComposing
        && this.canRequestArtwork() && this.hasValidArtworkContext()
        && VARIANT_ID_PATTERN.test(variantId) && !this.variantInput.disabled
        && this.lastReadyFingerprint
        && this.lastReadyFingerprint === this.artworkFingerprint(variantId)
        && this.image?.getAttribute('src')
        && this.image.complete && this.image.naturalWidth > 0
      );
    }

    clearImage({ retainReadyArtwork = false } = {}) {
      if (!this.customAssigned) this.setSupplierPreviewPresentation('closed');
      if (!retainReadyArtwork) {
        this.lastReadyFingerprint = '';
        this.image?.removeAttribute('src');
      }
      if (this.image) this.image.hidden = true;
      if (this.placeholder) this.placeholder.hidden = false;
      if (this.frame) this.frame.hidden = true;
    }

    showPlaceholderFrame() {
      this.showNeutralFrame();
    }

    showLoadingFrame() {
      this.showNeutralFrame();
    }

    showNeutralFrame() {
      if (!this.customAssigned) this.setSupplierPreviewPresentation('loading');
      this.lastReadyFingerprint = '';
      this.image.removeAttribute('src');
      this.image.hidden = true;
      this.placeholder.hidden = false;
      this.frame.hidden = false;
    }

    setLoading(isLoading) {
      this.previewBusy = Boolean(isLoading);
      if (this.progress) this.progress.hidden = !this.previewBusy;
      if (this.mobileProgress) this.mobileProgress.hidden = !this.previewBusy;
      this.mobileActions?.setAttribute('aria-busy', String(this.previewBusy));
      this.presentation?.setAttribute('aria-busy', String(this.previewBusy));
      if (this.button) {
        this.button.disabled = false;
        if (this.previewBusy) {
          this.button.setAttribute('aria-disabled', 'true');
        } else {
          this.button.removeAttribute('aria-disabled');
        }
      }
      this.setAttribute('aria-busy', String(this.previewBusy));
    }

    setStatus(copy) {
      const next = copy || '';
      if (this.status && this.status.textContent !== next) {
        this.status.textContent = next;
      }
    }

    normalise(value) {
      return String(value ?? '').normalize('NFC');
    }

    previewValue(control) {
      const value = String(control?.value ?? '');
      return value.trim().length > 0 ? value : '';
    }

    now() {
      return Date.now();
    }

    toCanonicalInteger(value, allowZero) {
      const text = String(value ?? '');
      const pattern = allowZero ? /^(?:0|[1-9]\d*)$/ : /^[1-9]\d*$/;
      if (!pattern.test(text)) return null;
      const number = Number(text);
      return Number.isSafeInteger(number) ? number : null;
    }
  }

  customElements.define('hf-personalisation-preview', HFPersonalisationPreview);
})();
